/**
 * ============================================================
 * COMBO PACK BUILDER (v3.2 — interception fix + debug) — assets/combo-pack-builder.js
 * Cupid Clothings | class-based, no globals, no jQuery
 *
 * PRODUCT SOURCE (unchanged): identical to the "Similar Products" section —
 * Shopify Storefront GraphQL API queried by the series tag resolved from
 * product.tags. NO recommendations / collections / search APIs.
 *
 * CHANGE vs v3 (BUG FIX): submit interception moved from the form element
 * to a DOCUMENT-level capture listener. Dawn's <product-form> registers its
 * own submit handler in its constructor (before our init), so at the form
 * element Dawn fired FIRST and submitted FormData WITHOUT line item
 * properties — which is why cart.js showed properties: {}. Document-capture
 * always runs before form-level listeners, so the combo payload (with
 * _combo_offer / _combo_id) is now the request that reaches cart/add.js.
 *
 * TEMP DEBUG logs included — search "DEBUG" to strip them after verifying.
 * ============================================================
 */

(function () {
  'use strict';

  /* Must match the Similar Products section config exactly */
  const SF = {
    storefrontToken: '4b701a000833fddc73ba74c9e713435e',
    apiVersion: '2024-04',
    maxProducts: 12,
  };

  const DISCOUNTS = { 1: 0, 2: 5, 3: 10 };

  /* TEMP DEBUG — set false / remove after verification */
  const DEBUG = true;

  const $  = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];

  /* price in paise -> ₹ display */
  const fmt = (paise) => {
    const n = (paise / 100).toFixed(2);
    const [i, d] = n.split('.');
    return `₹${i.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${d}`;
  };

  /* ── Series-tag resolution: copied from Similar Products logic ── */
  function findPatternBasedTag(tags) {
    for (const tag of tags) {
      if (/^_alt_/i.test(tag)) return tag;
      if (/^[A-Z]{1,3}K\d+$/i.test(tag)) return tag;
      if (/^[A-Z]{1,2}\d+$/i.test(tag)) return tag;
      if (/series|collection|line/i.test(tag) && /\d/.test(tag)) return tag;
      if (/^[mw]\s+\w+-reco$/i.test(tag)) return tag;
    }
    const collectionTags = [
      'men loungewear', 'men winterwear', 'men winter wear', 'T-SHIRTS', 't-shirts',
      'tshirts', 'Combos', 'combo pack', 'men activewear', 'men sleepwear',
      'men sweatpants', 'joggers', 'track pants',
    ];
    for (const tag of tags) {
      const lt = tag.toLowerCase().trim();
      for (const ct of collectionTags) {
        if (lt === ct.toLowerCase() || lt.includes(ct.toLowerCase())) return tag;
      }
    }
    return null;
  }

  function isCommonTag(tag) {
    const common = [
      'men', 'women', 'new', 'sale', 'hot', 'trending',
      'xl', 'xxl', '3xl', '4xl', '5xl', '6xl', '7xl', 'm', 'l', 's', 'xs',
      'cotton', 'polyester', 'blend',
      'black', 'white', 'blue', 'red', 'green', 'grey', 'gray',
      'track pants', 'trackpants', 'pants', 'trouser', 'trousers',
      'activewear', 'athleisure', 'sportswear',
      'lounge', 'loungewear', 'casual', 'formal',
      'joggers', 'sweatpants', 'pajama', 'pyjama',
    ];
    return common.some(c => tag.toLowerCase().includes(c.toLowerCase()));
  }

  async function gql(query, variables) {
    const res = await fetch(`${window.Shopify.routes.root}api/${SF.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SF.storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
    });
    return res.json();
  }

  async function tagHasProducts(tag, currentHandle) {
    try {
      const data = await gql(
        `query t($q:String!){products(first:3,query:$q){edges{node{handle}}}}`,
        { q: `tag:${tag}` }
      );
      const edges = data?.data?.products?.edges || [];
      return edges.some(e => e.node.handle !== currentHandle);
    } catch { return false; }
  }

  async function resolveSeriesTag(tags, currentHandle) {
    const patternTag = findPatternBasedTag(tags);
    if (patternTag) return patternTag;
    for (const tag of tags) {
      if (isCommonTag(tag)) continue;
      // eslint-disable-next-line no-await-in-loop
      if (await tagHasProducts(tag, currentHandle)) return tag;
    }
    return null;
  }

  /* ═══════════════════════════════════════════════════════════ */
  class CupidCombo {
    constructor(root) {
      this.root = root;

      const dataEl = $('#cupid-combo-product-data', root);
      if (!dataEl) return;
      this.current = JSON.parse(dataEl.textContent);

      this.tags = (root.dataset.series || '').split(',').map(t => t.trim()).filter(Boolean);
      this.currentHandle = root.dataset.currentHandle;

      this.packSize  = 1;
      this.related   = null;
      this.rendered  = false;            // dropdown options built once
      this.selection = { 2: null, 3: null };
      this.optSel    = { 1: {}, 2: {}, 3: {} };

      this._init();
    }

    _init() {
      this._bindPacks();
      this._bindDropdowns();
      this._bindItem1Options();
      this._hookProductForm();   // hook EXISTING PDP add-to-cart, no own button
      this._bindOutsideClose();
      this._updatePrice();
      this._loadRelated(); // one-time fetch
    }

    /* ── PACK TABS ── */
    _bindPacks() {
      $$('.cupid-combo-pack', this.root).forEach(tab => {
        tab.addEventListener('click', () => this._switchPack(parseInt(tab.dataset.pack, 10)));
      });
    }

    _switchPack(n) {
      this.packSize = n;
      $$('.cupid-combo-pack', this.root).forEach(t => {
        t.setAttribute('aria-selected', parseInt(t.dataset.pack, 10) === n ? 'true' : 'false');
      });
      $$('.cupid-combo-item', this.root).forEach(row => {
        const idx = parseInt(row.dataset.item, 10);
        if (idx === 1) return;
        row.classList.toggle('cupid-combo-item-hidden', idx > n);
      });
      this._closeAll();
      this._clearError();
      this._updatePrice();
    }

    /* ── ITEM 1 (locked) option dropdowns ── */
    _bindItem1Options() {
      $$('.cupid-combo-opt-select[data-item="1"]', this.root).forEach((sel, i) => {
        const name = (this.current.options || [])[i] || `option${i + 1}`;
        this.optSel[1][name] = sel.value;
        sel.addEventListener("change", () => {
        this.optSel[1][name] = sel.value;
        const variant = this._currentVariant();
        if (variant) {
        const priceEl = document.getElementById("cupid-combo-price-1");
        if (priceEl) {
        priceEl.textContent = fmt(Number(variant.price));
    }
  }

  this._updatePrice();
  this._clearError();
});
      });
    }

    /* ═══════════════════════════════════════════════════════════
     * PRODUCT SOURCE — Storefront GraphQL by series tag (UNCHANGED).
     * ═══════════════════════════════════════════════════════════ */
    async _loadRelated() {
      try {
        const seriesTag = await resolveSeriesTag(this.tags, this.currentHandle);
        if (!seriesTag) { this.related = []; this._populateDropdowns(); return; }

        const data = await gql(
          `query rel($q:String!,$first:Int!){
            products(first:$first,query:$q){
              edges{node{
                id
                title
                handle
                availableForSale
                featuredImage{ url altText }
                options{ name values }
                variants(first:100){edges{node{
                  id
                  title
                  availableForSale
                  price{ amount }
                  selectedOptions{ name value }
                }}}
              }}
            }
          }`,
          { q: `tag:${seriesTag}`, first: SF.maxProducts }
        );

        if (data.errors) { console.error('[CupidCombo] GraphQL', data.errors); this.related = []; this._populateDropdowns(); return; }

        const edges = data?.data?.products?.edges || [];

        /* Same filter rule as Similar Products: exclude current handle, require >= 2 available variants */
        this.related = edges
          .map(e => e.node)
          .filter(p => p.handle !== this.currentHandle)
          .filter(p => p.variants.edges.filter(v => v.node.availableForSale).length >= 2)
          .map(p => this._normalize(p));

        this._populateDropdowns();
      } catch (err) {
        console.error('[CupidCombo] load related failed', err);
        this.related = [];
        this._populateDropdowns();
      }
    }

    _normalize(p) {
      const variants = p.variants.edges.map(({ node: v }) => {
        const opts = {};
        (v.selectedOptions || []).forEach(o => { opts[o.name] = o.value; });
        return {
          id: this._gidToId(v.id),
          gid: v.id,
          title: v.title,
          available: v.availableForSale,
          price: Math.round(parseFloat(v.price.amount) * 100),
          options: opts,
        };
      });
      const firstAvail = variants.find(v => v.available) || variants[0];
      return {
        id: this._gidToId(p.id),
        handle: p.handle,
        title: p.title,
        image: p.featuredImage ? p.featuredImage.url : '',
        optionNames: (p.options || []).map(o => o.name),
        optionValues: (p.options || []).map(o => o.values),
        variants,
        basePrice: firstAvail ? firstAvail.price : 0,
      };
    }

    _gidToId(gid) {
      const m = String(gid).match(/\/(\d+)$/);
      return m ? parseInt(m[1], 10) : gid;
    }

    /* ═══════════════════════════════════════════════════════════
     * CUSTOM IMAGE DROPDOWN
     * Build option rows ONCE; open/close reuses the same DOM.
     * ═══════════════════════════════════════════════════════════ */
    _populateDropdowns() {
      if (this.rendered) return;
      this.rendered = true;

      $$('.cupid-combo-dd', this.root).forEach(dd => {
        const menu = $('.cupid-combo-dd-menu', dd);
        if (!menu) return;
        menu.innerHTML = '';

        if (!this.related || !this.related.length) {
          const li = document.createElement('li');
          li.className = 'cupid-combo-dd-empty';
          li.textContent = 'No related products found';
          menu.appendChild(li);
          const lbl = $('.cupid-combo-dd-trigger-label', dd);
          if (lbl) lbl.textContent = 'No related products';
          const trigger = $('.cupid-combo-dd-trigger', dd);
          if (trigger) trigger.disabled = true;
          return;
        }

        this.related.forEach((p, idx) => {
          const li = document.createElement('li');
          li.className = 'cupid-combo-dd-option';
          li.setAttribute('role', 'option');
          li.dataset.index = String(idx);
          li.innerHTML = `
            <span class="cupid-combo-dd-opt-thumb">
              ${p.image ? `<img src="${p.image}" alt="${p.title}" loading="lazy" />` : ''}
            </span>
            <span class="cupid-combo-dd-opt-info">
              <span class="cupid-combo-dd-opt-title">${p.title}</span>
              <span class="cupid-combo-dd-opt-price">${fmt(p.basePrice)}</span>
            </span>
          `;
          li.addEventListener('click', () => {
            const slot = parseInt(dd.dataset.item, 10);
            this._setTrigger(dd, p);
            this._markActive(menu, li);
            this._closeDropdown(dd);
            this._chooseProduct(slot, p);
          });
          menu.appendChild(li);
        });
      });
    }

    _bindDropdowns() {
      $$('.cupid-combo-dd', this.root).forEach(dd => {
        const trigger = $('.cupid-combo-dd-trigger', dd);
        trigger && trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          if (trigger.disabled) return;
          const isOpen = dd.classList.contains('cupid-combo-dd-open');
          this._closeAll();
          if (!isOpen) this._openDropdown(dd);
        });
      });
    }

    _openDropdown(dd) {
      dd.classList.add('cupid-combo-dd-open');
      const trigger = $('.cupid-combo-dd-trigger', dd);
      const menu = $('.cupid-combo-dd-menu', dd);
      trigger && trigger.setAttribute('aria-expanded', 'true');
      menu && menu.classList.remove('cupid-combo-hidden');
    }

    _closeDropdown(dd) {
      dd.classList.remove('cupid-combo-dd-open');
      const trigger = $('.cupid-combo-dd-trigger', dd);
      const menu = $('.cupid-combo-dd-menu', dd);
      trigger && trigger.setAttribute('aria-expanded', 'false');
      menu && menu.classList.add('cupid-combo-hidden');
    }

    _closeAll() {
      $$('.cupid-combo-dd', this.root).forEach(dd => this._closeDropdown(dd));
    }

    _bindOutsideClose() {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.cupid-combo-dd')) this._closeAll();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._closeAll();
      });
    }

    /* Update the trigger to show selected product (thumb + name) */
    _setTrigger(dd, prod) {
      const thumbWrap = $('.cupid-combo-dd-trigger-thumb', dd);
      const thumbImg  = thumbWrap ? $('img', thumbWrap) : null;
      const label     = $('.cupid-combo-dd-trigger-label', dd);
      if (thumbImg) { thumbImg.src = prod.image || ''; thumbImg.alt = prod.title; }
      if (thumbWrap) thumbWrap.classList.toggle('cupid-combo-hidden', !prod.image);
      if (label) {
        label.textContent = prod.title;
        label.classList.remove('cupid-combo-dd-placeholder');
      }
    }

    _markActive(menu, activeLi) {
      $$('.cupid-combo-dd-option', menu).forEach(li => li.classList.remove('cupid-combo-dd-active'));
      activeLi.classList.add('cupid-combo-dd-active');
    }

    /* ── PRODUCT chosen → render SIZE dropdown only (dynamic) ── */
    _chooseProduct(slot, prod) {
      this.selection[slot] = prod;
      this.optSel[slot] = {};

      const chosen = $(`#cupid-combo-chosen-${slot}`, this.root);
      const img    = $('.cupid-combo-chosen-img', chosen);
      const name   = $('.cupid-combo-chosen-name', chosen);
      const price  = $('.cupid-combo-chosen-price', chosen);
      const optsEl = $(`#cupid-combo-opts-${slot}`, this.root);

      if (img)  { img.src = prod.image; img.alt = prod.title; }
      if (name) name.textContent = prod.title.length > 38 ? prod.title.slice(0, 36) + '…' : prod.title;
      if (price) price.textContent = fmt(prod.basePrice);

      if (optsEl) {
        optsEl.innerHTML = '';

        /* SIZE DETECTION: find the option whose name contains "size"
           (any casing) and render ONLY that. All other options (Color,
           etc.) are ignored. null = no Size option → render nothing. */
        const sizeName = this._detectSizeOption(prod);

        if (sizeName) {
          /* Only sizes whose variant is available (variant.available === true).
             Sold-out sizes are never rendered — no fallback. */
          const allSizes = [...new Set(
            prod.variants
              .filter(v => v.available)
              .map(v => v.options[sizeName])
              .filter(Boolean)
          )];

          if (allSizes.length) {
            const wrap = document.createElement('div');
            wrap.className = 'cupid-combo-opt';

            const select = document.createElement('select');
            select.className = 'cupid-combo-select';
            select.setAttribute('aria-label', sizeName);

            allSizes.forEach((val, vi) => {
              select.appendChild(new Option(`${sizeName}: ${val}`, val));
              if (vi === 0) this.optSel[slot][sizeName] = val;
            });

           select.addEventListener("change", () => {
        this.optSel[slot][sizeName] = select.value;
        const variant = this._resolveVariant(slot);

       if (variant) {
       const price = chosen.querySelector(".cupid-combo-chosen-price");
       if (price) {
      price.textContent = fmt(Number(variant.price));
    }
  }

  this._updatePrice();
  this._clearError();
});

            wrap.appendChild(select);
            optsEl.appendChild(wrap);
          }
        }
      }

      chosen && chosen.classList.remove('cupid-combo-hidden');
      this._updatePrice();
      this._clearError();
    }

    /* Detect the option whose name contains "size" (any casing).
       Returns ONLY that option name. If no Size option exists, returns
       null so nothing is rendered — Color and other options are ignored. */
    _detectSizeOption(prod) {
      const names = prod.optionNames || [];
      return names.find(n => /size/i.test(n)) || null;
    }

    /* ── Variant resolution (items 2/3 resolve by SIZE only) ── */
    _resolveVariant(slot) {
      if (slot === 1) return this._currentVariant();
      const prod = this.selection[slot];
      if (!prod) return null;
      if (prod.variants.length === 1) return prod.variants[0];

      const sizeName = this._detectSizeOption(prod);
      const sel = this.optSel[slot] || {};
      const chosenSize = sizeName ? sel[sizeName] : null;
      if (!chosenSize) return null;

      /* Match the variant whose Size equals the chosen size.
         Prefer an available variant if duplicates exist. */
      const matches = prod.variants.filter(v => v.options[sizeName] === chosenSize);
      if (!matches.length) return null;
      return matches.find(v => v.available) || matches[0];
    }

   _currentVariant() {
  const variants = this.current.variants || [];

  const size = this.optSel[1]["Size"] || this.optSel[1]["size"];
  if (!size) {
    return variants.find(v => v.available) || variants[0] || null;
  }

  return (
    variants.find(v => v.option1 === size) ||
    variants.find(v => v.available) ||
    variants[0] ||
    null
  );
}

    /* ── PRICE (UNCHANGED) ── */
    _updatePrice() {
      const slots = [1];
      if (this.packSize >= 2) slots.push(2);
      if (this.packSize >= 3) slots.push(3);

      let mrp = 0;
      slots.forEach(slot => {
        const v = this._resolveVariant(slot);
        if (v) mrp += parseInt(v.price, 10);
        else if (slot !== 1 && this.selection[slot]) mrp += this.selection[slot].basePrice;
      });

      const pct  = DISCOUNTS[this.packSize] || 0;
      const save = Math.round(mrp * pct / 100);
      const total = mrp - save;

      const mrpEl = $('#cupid-combo-mrp', this.root);
      const totEl = $('#cupid-combo-total', this.root);
      const saveWrap = $('#cupid-combo-save-wrap', this.root);
      const saveEl = $('#cupid-combo-save', this.root);

      if (mrpEl) mrpEl.textContent = fmt(mrp);
      if (totEl) totEl.textContent = fmt(total);
      if (saveWrap) {
        const show = pct > 0 && save > 0;
        saveWrap.classList.toggle('cupid-combo-hidden', !show);
        if (saveEl) saveEl.textContent = `−${fmt(save)}`;
      }
    }

    /* ── VALIDATION (UNCHANGED) ── */
    _validate() {
      const slots = [1];
      if (this.packSize >= 2) slots.push(2);
      if (this.packSize >= 3) slots.push(3);

      for (const slot of slots) {
        if (slot !== 1 && !this.selection[slot]) {
          return `Please select a product for item 0${slot}.`;
        }
        const v = this._resolveVariant(slot);
        if (!v) return `Please choose all options for item 0${slot}.`;
        if (v.available === false) return `Item 0${slot} variant is out of stock.`;
      }
      return null;
    }

    /* ═══════════════════════════════════════════════════════════
     * HOOK EXISTING PDP ADD-TO-CART — v3.2 FIX
     *
     * WHY DOCUMENT-LEVEL CAPTURE:
     * Dawn's <product-form> custom element registers its own submit
     * handler in its CONSTRUCTOR — which runs at element upgrade
     * during HTML parsing, BEFORE this script's DOMContentLoaded
     * init. When the event target IS the form, capture/bubble
     * listeners attached to the form all fire in REGISTRATION ORDER
     * (target phase), so Dawn fired first, preventDefault'ed, and
     * submitted new FormData(form) — which carries NO line item
     * properties. That request is what created the cart lines, hence
     * properties: {} in cart.js.
     *
     * A capture listener on document runs in the CAPTURE PHASE,
     * which always executes before any listener on the form itself,
     * regardless of registration order. stopPropagation() here means
     * Dawn's handler never sees the event for pack 2/3.
     * ═══════════════════════════════════════════════════════════ */
    _hookProductForm() {
      const form = document.querySelector(
        'form[data-type="add-to-cart-form"], .product-form form, product-form form'
      );
      if (!form) { console.warn('[CupidCombo] product form not found'); return; }

      const productFormEl = form.closest('product-form');

      document.addEventListener('submit', (e) => {
        if (e.target !== form) return;       // only our PDP form

        /* Pack of 1 → native PDP flow, do nothing */
        if (this.packSize === 1) {
          DEBUG && console.log('[CupidCombo] pack 1 → native Dawn flow');
          return;
        }

        /* Kill the event BEFORE it reaches Dawn's product-form handler */
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        DEBUG && console.log('[CupidCombo] submit intercepted (document-capture) | packSize =', this.packSize);

        /* Combo validation reuses the combo error element */
        const err = this._validate();
        if (err) { this._showError(err); return; }

        this._submitCombo(form, productFormEl);
      }, true); // ← capture on document: beats Dawn, guaranteed
    }

    /* Build the line items for the active pack (each variant its own line).
       Every combo line carries a hidden property identifying the pack so the
       cart-drawer Liquid snippet can detect it and surface the coupon.
       Pack 1 carries no property (no coupon). */
    _buildItems() {
      const slots = [1];
      if (this.packSize >= 2) slots.push(2);
      if (this.packSize >= 3) slots.push(3);

      /* pack2 / pack3 tag; Pack 1 = no property */
      const comboTag = this.packSize >= 2 ? `pack${this.packSize}` : null;
      const comboId = comboTag ? `${comboTag}-${Date.now()}` : null;

      const items = [];
      const seen = new Set();
      for (const slot of slots) {
        const variant = this._resolveVariant(slot);
        if (!variant) continue;
        const vid = Number(variant.id);

        if (seen.has(vid)) {
          const ex = items.find(it => it.id === vid);
          if (ex) ex.quantity += 1;
        } else {
          const line = { id: vid, quantity: 1 };
          if (comboTag) {
            /* Hidden line item properties. Leading underscore = hidden from
               cart/checkout DISPLAY but still present in cart.js JSON and
               accessible in Liquid via item.properties._combo_offer.
               _combo_offer drives coupon detection; _combo_id groups the
               lines of one combo together. */
            line.properties = {
              _combo_offer: comboTag,
              _combo_id: comboId,
            };
          }
          items.push(line);
          seen.add(vid);
        }
      }

      console.log('COMBO ITEMS', JSON.parse(JSON.stringify(items)));
      return items;
    }

    /* Send the combo payload ourselves (Dawn's submit is blocked for
       pack 2/3 by the document-capture interceptor). Reuses Dawn's
       loading spinner, error wrapper, and cart-drawer rendering. */
    _submitCombo(form, productFormEl) {
      const items = this._buildItems();
      if (!items.length) { this._showError('Please select your products.'); return; }

      const config = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      };

      /* Reuse Dawn's loading state if present */
      const submitBtn = form.querySelector('[type="submit"]');
      const spinner = submitBtn ? submitBtn.querySelector('.loading-overlay__spinner, .loading__spinner') : null;
      if (productFormEl) productFormEl.toggleAttribute?.('loading', true);
      submitBtn && (submitBtn.setAttribute('aria-disabled', 'true'), submitBtn.classList.add('loading'), submitBtn.setAttribute('disabled', 'disabled'));
      spinner && spinner.classList.remove('hidden');

      /* Sections payload so the cart drawer re-renders, exactly like Dawn */
      const cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (cartEl && cartEl.getSectionsToRender) {
        const sections = cartEl.getSectionsToRender().map(s => s.id);
        const body = JSON.parse(config.body);
        body.sections = sections;
        body.sections_url = window.location.pathname;
        config.body = JSON.stringify(body);
      }

      console.log('ADD TO CART PAYLOAD', config.body);

      fetch(`${window.Shopify.routes.root}cart/add.js`, config)
        .then(r => r.json())
        .then(response => {
          console.log('CART RESPONSE', response);

          console.log(
  'DRAWER SECTIONS CONFIG',
  cartEl && cartEl.getSectionsToRender
    ? cartEl.getSectionsToRender()
    : 'No getSectionsToRender'
);

console.log(
  'RESPONSE SECTION KEYS',
  response.sections
    ? Object.keys(response.sections)
    : 'No sections returned'
);
          if (response.status) {
            /* Surface error through Dawn's own error wrapper if available */
            this._showFormError(form, response.description || response.message);
            return;
          }
          this._clearError();

          /* TEMP DEBUG: verify properties actually persisted in the cart */
          if (DEBUG) {
            fetch(`${window.Shopify.routes.root}cart.js`)
              .then(r => r.json())
              .then(cart => console.log('CART.JS VERIFY',
                cart.items.map(i => ({ vid: i.variant_id, qty: i.quantity, props: i.properties }))));
          }

          /* Hand off to Dawn's cart UI — same render path as native ATC.
             The coupon banner ("🎉 COMBO5/COMBO10 unlocked") is rendered by
             the cart-drawer Liquid snippet, which reads _combo_offer from
             cart line properties on every drawer re-render. */
          if (cartEl) {
            if (cartEl.renderContents) cartEl.renderContents(response);
            if (typeof cartEl.open === 'function') cartEl.open();
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        })
        .catch(() => this._showFormError(form, 'Something went wrong. Please try again.'))
        .finally(() => {
          if (productFormEl) productFormEl.toggleAttribute?.('loading', false);
          submitBtn && (submitBtn.removeAttribute('aria-disabled'), submitBtn.classList.remove('loading'), submitBtn.removeAttribute('disabled'));
          spinner && spinner.classList.add('hidden');
        });
    }

    /* Show error in Dawn's product-form error wrapper, falling back to combo error */
    _showFormError(form, msg) {
      const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const span = wrapper?.querySelector('.product-form__error-message');
      if (wrapper && span) {
        span.textContent = msg;
        wrapper.removeAttribute('hidden');
      } else {
        this._showError(msg);
      }
    }

    _showError(msg) {
      const el = $('#cupid-combo-error', this.root);
      if (!el) return;
      el.textContent = msg;
      el.classList.remove('cupid-combo-hidden');
    }
    _clearError() {
      const el = $('#cupid-combo-error', this.root);
      el && el.classList.add('cupid-combo-hidden');
    }
  }

  /* ── Bootstrap ── */
  function init() {
    const root = document.getElementById('cupid-combo-root');
    if (!root || root._cupidComboInit) return;
    root._cupidComboInit = true;
    new CupidCombo(root);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', () => {
    const root = document.getElementById('cupid-combo-root');
    if (root) root._cupidComboInit = false;
    init();
  });
})();